import { ODSCheckbox } from 'kfone-component-library';
import type { RtiComparisonGroupModel } from 'model/interfaces/rti-comparison-group';
import type { RtiCustomFieldToggle, RtiFieldLeaf } from 'model/interfaces/rti-employee-data-fields';
import { getRtiFieldLabel, isCustomFieldValue } from 'utilities/rti-employee-data-fields';
import { getOrderedComparisonGroupJobCompanyEntries } from 'utilities/rti-comparison-group';
import { useCallback, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export type RtiComparisonGroupsProps = {
  value: RtiComparisonGroupModel;
  onChange: (next: RtiComparisonGroupModel) => void;
  disabled?: boolean;
};

function FieldRowBoolean(props: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { id, label, checked, onChange } = props;
  return (
    <div className='flex flex-col gap-1'>
      <ODSCheckbox
        id={id}
        checkList={[
          {
            label,
            value: id,
            checked,
          },
        ]}
        onChange={(checkbox) => {
          onChange(checkbox?.checked ?? false);
        }}
      />
    </div>
  );
}

function FieldRowCustom(props: {
  id: string;
  fieldKey: string;
  data: RtiCustomFieldToggle;
  value: RtiComparisonGroupModel;
  onChange: (next: RtiComparisonGroupModel) => void;
  disabled?: boolean;
}) {
  const { t } = useTranslation();
  const { id, fieldKey, data, value, onChange, disabled } = props;

  const checkboxLabel = getRtiFieldLabel(fieldKey, t);

  const patch = useCallback(
    (nextLeaf: RtiCustomFieldToggle) => {
      onChange({
        ...value,
        job_company_information: {
          ...value.job_company_information,
          [fieldKey]: nextLeaf,
        },
      });
    },
    [fieldKey, onChange, value],
  );

  const setEnabled = useCallback(
    (checked: boolean) => {
      patch({ ...data, enabled: checked });
    },
    [data, patch],
  );

  const setLabel = useCallback(
    (label: string) => {
      patch({ ...data, label });
    },
    [data, patch],
  );

  return (
    <div className='flex flex-col gap-2'>
      <FieldRowBoolean
        id={`${id}-en`}
        label={checkboxLabel}
        checked={data.enabled}
        onChange={setEnabled}
      />
      <input
        type='text'
        className='ml-8 w-full max-w-[220px] rounded border border-[#C5C7CA] px-2 py-1.5 font-ods-font-family-secondary text-ods-font-styles-body-regular-small text-[#1D1D1D] placeholder:text-[#6C6F75] disabled:cursor-not-allowed disabled:bg-[#F5F5F5] disabled:opacity-60'
        value={data.label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder={checkboxLabel}
        disabled={disabled || !data.enabled}
        aria-label={t('pe.settings.rti.employeeData.custom.labelAria')}
      />
    </div>
  );
}

function ColumnHeader({ children }: { children: ReactNode }) {
  return (
    <p className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal uppercase text-[#5D5C5C]'>
      {children}
    </p>
  );
}

export function RtiComparisonGroups({ value, onChange, disabled }: RtiComparisonGroupsProps) {
  const { t } = useTranslation();

  const updateLeaf = useCallback(
    (fieldKey: string, leaf: RtiFieldLeaf) => {
      onChange({
        ...value,
        job_company_information: {
          ...value.job_company_information,
          [fieldKey]: leaf,
        },
      });
    },
    [onChange, value],
  );

  const entries = getOrderedComparisonGroupJobCompanyEntries(value.job_company_information ?? {});

  return (
    <section className='flex flex-col gap-4' aria-labelledby='rti-comparison-groups-title'>
      <h3
        id='rti-comparison-groups-title'
        className='font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-bold text-[#1D1D1D]'
      >
        {t('pe.settings.rti.comparisonGroups.title')}
      </h3>
      <p className='-mt-2 font-ods-font-family-secondary text-ods-font-styles-body-regular-small font-normal text-[#1D1D1D]'>
        {t('pe.settings.rti.comparisonGroups.helper')}
      </p>

      <div className={disabled ? 'flex min-w-0 flex-col gap-4 pointer-events-none opacity-60' : 'flex min-w-0 flex-col gap-4'}>
        <ColumnHeader>{t('pe.settings.rti.jobCompanyInformation.title')}</ColumnHeader>
        <div className='flex max-w-xl flex-col gap-4'>
          {entries.map(([fieldKey, leaf]) => {
            const rowId = `rti-cg-jci-${fieldKey}`.replace(/[^a-zA-Z0-9_-]/g, '_');
            if (typeof leaf === 'boolean') {
              const booleanLabel = getRtiFieldLabel(fieldKey, t);
              return (
                <FieldRowBoolean
                  key={fieldKey}
                  id={rowId}
                  label={booleanLabel}
                  checked={leaf}
                  onChange={(checked) => updateLeaf(fieldKey, checked)}
                />
              );
            }
            if (isCustomFieldValue(leaf)) {
              return (
                <FieldRowCustom
                  key={fieldKey}
                  id={rowId}
                  fieldKey={fieldKey}
                  data={leaf}
                  value={value}
                  onChange={onChange}
                  disabled={disabled}
                />
              );
            }
            return null;
          })}
        </div>
      </div>
    </section>
  );
}
